import React from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import Checkbox from 'common/components/atoms/Checkbox';
import FlexContainer from 'common/components/atoms/FlexContainer';
import InfoTooltip from 'common/components/atoms/InfoTooltip';
import { Plus } from 'common/images/FaIcons';
import DataTable from 'common/components/organisms/DataTable';
import FilterBar from 'common/components/organisms/FilterBar';
import FilterCountContainer from 'common/components/atoms/FilterCountContainer';
import FilterItem from 'common/components/atoms/FilterItem';
import MsgContainer from 'common/components/atoms/MsgContainer';
import SearchFilters from 'common/components/atoms/SearchFilters';
import { PrimaryButton, ToolbarSearchBox } from 'common/components/molecules/ListToolbar';

import CreateAlarmParameterModal from './CreateAlarmParameterModal';
import { getAlarmTagTypes } from '../alarmParameters.service';
import {
  applyFilters,
  featureLabel,
  filterOptions,
  filterValueLabel,
  midSentence,
  sortRows,
  tagTypeLabel
} from '../constants/alarmParameters.constants';
import {
  BannerActions,
  BannerLink,
  BannerText,
  Dismiss,
  ErrorBanner,
  Page,
  StatusBanner,
  TableArea
} from './AlarmParameters.styled';

const ABOUT = 'Each row is a measure that customers can set an overall alarm on. '
  + 'Create one and every account with that tag type can set warning and '
  + 'critical limits for it under Charts > Alarms. No release is needed.';

const T = 'adminDashboard.alarmParameters.';

const FILTER_FIELDS = ['tag_type', 'feature', 'amplitude_type'];

const emptyFilters = () => ({ tag_type: [], feature: [], amplitude_type: [] });

/**
 * Every alarm parameter registered today, with the keys it actually uses.
 *
 * Same building blocks as the devices page: the SearchFilters header, the
 * FilterBar and DataTable. Rows are never deleted from here - the events
 * service refuses once an account uses one, and the ops app owns that.
 */
class AlarmParameters extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      tagTypes: [],
      rows: [],
      filters: emptyFilters(),
      search: '',
      sort: null,
      loading: true,
      showModal: false,
      created: null,
      error: null
    };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ loading: true });

    // handleResponse resolves with response.data, so this is already the
    // payload rather than the axios response.
    getAlarmTagTypes().then(
      res => this.setState({
        tagTypes: (res && res.tag_types) || [],
        rows: (res && res.enabled) || [],
        loading: false,
        error: null
      }),
      error => this.setState({
        loading: false,
        error: error && error.message
          ? error.message
          : this.props.t(`${T}loadError`, { defaultValue: 'Could not load alarm parameters.' })
      })
    );
  };

  onCreated = (row) => {
    this.setState({ showModal: false, created: row || null });
    this.load();
  };

  fieldTitle = (field) => {
    const { t } = this.props;
    if (field === 'tag_type') return t(`${T}columns.tagType`, { defaultValue: 'Tag type' });
    if (field === 'feature') return t(`${T}columns.feature`, { defaultValue: 'Feature' });

    return t(`${T}columns.amplitude`, { defaultValue: 'Amplitude' });
  };

  toggleFilterValue = (field, value) => {
    this.setState((prev) => {
      const selected = prev.filters[field] || [];
      const next = selected.indexOf(value) === -1
        ? selected.concat([value])
        : selected.filter(item => item !== value);

      return { filters: Object.assign({}, prev.filters, { [field]: next }) };
    });
  };

  clearFilter = (field) => {
    this.setState(prev => ({
      filters: Object.assign({}, prev.filters, { [field]: [] })
    }));
  };

  clearAllFilters = () => {
    this.setState({ filters: emptyFilters() });
  };

  filterFields = () => FILTER_FIELDS.map(name => ({ name, title: this.fieldTitle(name) }));

  appliedChips = () => {
    const { filters } = this.state;

    return FILTER_FIELDS
      .filter(field => filters[field] && filters[field].length)
      .map(field => ({
        name: field,
        title: this.fieldTitle(field),
        text: filters[field].map(value => filterValueLabel(field, value)).join(', ')
      }));
  };

  renderFieldOptions = (field) => {
    const options = filterOptions(this.state.rows, field);
    const selected = this.state.filters[field] || [];

    if (!options.length) {
      return (
        <MsgContainer>
          <span>{this.props.t(`${T}noFilterValues`, { defaultValue: 'Nothing to filter on' })}</span>
        </MsgContainer>
      );
    }

    return (
      <>
        {options.map(option => (
          <FilterItem
            key={option.value}
            className="dropdown-item"
            selected={selected.indexOf(option.value) !== -1}
            onClick={() => this.toggleFilterValue(field, option.value)}
          >
            <Checkbox inputField value={selected.indexOf(option.value) !== -1} />
            <span>{option.text}</span>
          </FilterItem>
        ))}
      </>
    );
  };

  buildColumns = () => [
    {
      name: 'tag_type',
      label: this.fieldTitle('tag_type'),
      render: row => tagTypeLabel(row.tag_type)
    },
    {
      name: 'feature',
      label: this.fieldTitle('feature'),
      render: row => featureLabel(row.feature)
    },
    {
      name: 'amplitude_type',
      label: this.fieldTitle('amplitude_type'),
      // Stored per row rather than parsed from the name. For broadband the two
      // agree, because the measurement column embeds it (rms_velocity). They
      // stop agreeing for narrowband, where shaft_speed_1x exists as two
      // feature_definition rows with the same name and different amplitudes.
      render: row => (row.amplitude_type ? featureLabel(row.amplitude_type) : '--')
    }
  ];

  renderCreatedBanner() {
    const { created } = this.state;
    if (!created) return null;
    const { t } = this.props;

    return (
      <StatusBanner>
        <BannerText>
          <strong>
            {t(`${T}added`, {
              defaultValue: '{{feature}} added',
              feature: featureLabel(created.feature)
            })}
          </strong>
          {' '}
          {t(`${T}addedNext`, {
            defaultValue: 'Customers set it per tag in Charts → Alarms on {{tagType}} tags. '
              + 'A charts page that is already open needs a refresh to see it.',
            tagType: midSentence(tagTypeLabel(created.tag_type))
          })}
        </BannerText>
        <BannerActions>
          <BannerLink onClick={() => this.props.history.push('/assets')}>
            {t(`${T}openCharts`, { defaultValue: 'Open charts' })}
          </BannerLink>
          <Dismiss
            type="button"
            aria-label={t(`${T}dismiss`, { defaultValue: 'Dismiss' })}
            onClick={() => this.setState({ created: null })}
          >
            &times;
          </Dismiss>
        </BannerActions>
      </StatusBanner>
    );
  }

  render() {
    const { t } = this.props;
    const {
      tagTypes, rows, filters, search, loading, showModal, error
    } = this.state;
    const visible = sortRows(applyFilters(rows, filters, search), this.state.sort);

    return (
      <Page>
        <SearchFilters>
          <FlexContainer alignItems="center" style={{ width: '100%' }}>
            <FilterCountContainer>{visible.length}</FilterCountContainer>
            <InfoTooltip
              label={t(`${T}aboutLabel`, { defaultValue: 'About alarm parameters' })}
              content={t(`${T}about`, { defaultValue: ABOUT })}
            />
            <FlexContainer alignItems="center" style={{ marginLeft: 'auto', gap: '8px' }}>
              <ToolbarSearchBox
                name="alarm-type-search-box"
                placeholder={t(`${T}searchPlaceholder`, { defaultValue: 'Search features' })}
                value={search}
                onChange={e => this.setState({ search: e.target.value })}
                clearLabel={t('common.clear', { defaultValue: 'Clear' })}
              />
              <PrimaryButton type="button" onClick={() => this.setState({ showModal: true })}>
                <Plus />
                {t(`${T}createButton`, { defaultValue: 'Create alarm parameter' })}
              </PrimaryButton>
            </FlexContainer>
          </FlexContainer>
        </SearchFilters>

        <FilterBar
          labels={{
            addFilter: t(`${T}filter`, { defaultValue: 'Filters' }),
            clearAll: t(`${T}clearFilters`, { defaultValue: 'Clear all' })
          }}
          testIds={{ addButton: 'alarm-type-filter-add-button', fieldOption: 'alarm-type-filter-field-option' }}
          filterFields={this.filterFields()}
          renderFieldOptions={this.renderFieldOptions}
          chips={this.appliedChips()}
          onRemoveChip={this.clearFilter}
          onClearAll={this.clearAllFilters}
        />

        {error && <ErrorBanner>{error}</ErrorBanner>}
        {this.renderCreatedBanner()}

        <TableArea>
          <DataTable
            columns={this.buildColumns()}
            rows={visible}
            sort={this.state.sort}
            onSortChange={sort => this.setState({ sort })}
            getRowKey={row => `${row.tag_type}:${row.feature}:${row.amplitude_type || ''}`}
            loading={loading}
            emptyMessage={rows.length
              ? t(`${T}noMatch`, { defaultValue: 'No alarm parameter matches these filters.' })
              : t(`${T}empty`, { defaultValue: 'No alarm parameters yet.' })}
          />
        </TableArea>

        {showModal && (
          <CreateAlarmParameterModal
            tagTypes={tagTypes}
            onClose={() => this.setState({ showModal: false })}
            onCreated={this.onCreated}
          />
        )}
      </Page>
    );
  }
}

AlarmParameters.propTypes = {
  t: PropTypes.func.isRequired,
  history: PropTypes.shape({ push: PropTypes.func }).isRequired
};

export default withRouter(withTranslation()(AlarmParameters));
